import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import { FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { ImageCropUtils } from '../../../components/ImageCrop';
import { ymdFromDate, ymdToLocalMidnight } from '../../../filters/date-filters';
import { createVolume, updateVolume, VolumeCommandValues } from '../api/saveVolume';
import { uploadCurriculumImage } from '../api/uploadCurriculumImage';
import { Volume } from '../models/volume';

type FormValues = {
  name: string;
  image: string;
  number?: number | string;
  periodTitle?: string;
  description?: string;
  posterUrl?: string;
  startDate?: Date;
  endDate?: Date;
  availabilityEndDate?: Date;
};

type FormStatus = {
  isAlwaysAvailable: boolean;
  descriptionEditorState: EditorState;
};

const transformVolumeToFormValues: (volume: Volume) => FormValues = volume => ({
  name: volume.name,
  image: volume.image,
  number: volume.number || '',
  periodTitle: volume.periodTitle || '',
  description: volume.description || '',
  posterUrl: volume.posterUrl || '',
  startDate: ymdToLocalMidnight(volume.startDate),
  endDate: ymdToLocalMidnight(volume.endDate),
  availabilityEndDate: volume.availabilityEndDate ? ymdToLocalMidnight(volume.availabilityEndDate) : undefined,
});

const transformFormValuesToCommandValues: (formValues: FormValues, posterFilename: string) => VolumeCommandValues = (
  formValues,
  posterFilename
) => {
  const getVolumeNumber = (volumeNumber: FormValues['number']) => {
    if (!volumeNumber) return undefined;
    if (typeof volumeNumber === 'string') {
      return parseInt(volumeNumber) || undefined;
    }
    return volumeNumber;
  };

  const availabilityEndDate = formValues.availabilityEndDate ? ymdFromDate(formValues.availabilityEndDate) : undefined;

  return {
    name: formValues.name,
    startDate: ymdFromDate(formValues.startDate),
    endDate: ymdFromDate(formValues.endDate),
    image: posterFilename,
    number: getVolumeNumber(formValues.number),
    description: formValues.description,
    posterUrl: formValues.posterUrl,
    periodTitle: formValues.periodTitle,
    availabilityEndDate,
  };
};

const emptyVolume = {
  name: '',
  image: '',
  startDate: undefined,
  endDate: undefined,
  number: '',
  description: '',
  periodTitle: '',
  availabilityEndDate: undefined,
  posterUrl: '',
};

export const useManageCurriculumVolumeForm = (volume?: Volume) => {
  const [initialFormValues] = useState<FormValues>(volume ? transformVolumeToFormValues(volume) : emptyVolume);
  const [initialFormStatus] = useState<FormStatus>({
    isAlwaysAvailable: initialFormValues.availabilityEndDate === undefined,
    descriptionEditorState: initialFormValues?.description
      ? EditorState.createWithContent(stateFromHTML(initialFormValues.description))
      : EditorState.createEmpty(),
  });

  const validationSchema = Yup.object({
    image: Yup.string().required(),
    name: Yup.string().trim().required('A subtitle is required'),
    number: Yup.string(),
    periodTitle: Yup.string(),
    description: Yup.string().nullable(),
    posterUrl: Yup.string(),
    startDate: Yup.date().required(),
    endDate: Yup.date()
      .required('An end date is required')
      .min(Yup.ref('startDate'), 'End date must come after start date'),
    availabilityEndDate: Yup.date()
      .nullable()
      .test('requireIfActive', 'An availability end date is required', value => value !== null),
  });

  const toggleAvailabilityEndDate = (
    isAlwaysAvailable: boolean,
    setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
    status: FormStatus,
    setStatus: FormikHelpers<FormValues>['setStatus']
  ) => {
    if (isAlwaysAvailable) {
      setFieldValue('availabilityEndDate', initialFormValues.availabilityEndDate || null);
    } else {
      setFieldValue('availabilityEndDate', undefined);
    }

    setStatus({ ...status, isAlwaysAvailable: !isAlwaysAvailable });
  };

  const updateTextEditor = (
    es: EditorState,
    fieldName: string,
    setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
    status: FormStatus,
    setStatus: FormikHelpers<FormValues>['setStatus']
  ) => {
    setStatus({ ...status, [`${fieldName}EditorState`]: es });

    const html = stateToHTML(es.getCurrentContent());
    setFieldValue(fieldName, html);
  };

  const uploadVolumePoster = (image: string | undefined, originalImage: string | undefined): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (ImageCropUtils.isCroppedImage(image)) {
        const filename = ImageCropUtils.generateFilename();
        ImageCropUtils.convertCroppedImageToBlob(image)
          .then(blob => uploadCurriculumImage(filename, blob))
          .then(() => resolve(filename))
          .catch(reason => reject(reason));
      } else if (originalImage) {
        resolve(originalImage);
      } else {
        reject(new Error('An image must be provided'));
      }
    });
  };

  const submitForm = (
    formValues: FormValues,
    curriculumId: string,
    userId: string,
    volumeId?: string,
    originalImage?: string
  ) => {
    return uploadVolumePoster(formValues.image, originalImage).then((posterFilename: string) => {
      const commandValues = transformFormValuesToCommandValues(formValues, posterFilename);
      return volumeId
        ? updateVolume(curriculumId, volumeId, commandValues, userId)
        : createVolume(curriculumId, commandValues, userId);
    });
  };

  return {
    updateTextEditor,
    toggleAvailabilityEndDate,
    initialFormValues,
    initialFormStatus,
    validationSchema,
    submitForm,
  };
};
